import apiClient from '../client'

/**
 * Normalize a notification list response to a plain array.
 *
 * The endpoints are paginated by JSONAPIPagination, so a paginated response is
 * `{ data: [...], meta, links }`. Unpaginated responses are a bare array, and
 * DRF's default pagination would use `results`. Anything else yields [] so
 * consumers can always rely on getting an array.
 *
 * @param {*} payload - Raw `response.data`
 * @returns {Array} Array of notification objects
 */
function toNotificationArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

/**
 * Fetch unread notifications for current user
 * @returns {Promise<Array>} Array of notification objects
 */
export async function fetchUnreadNotifications() {
  const response = await apiClient.get('notifications/unread/')
  return toNotificationArray(response.data)
}

/**
 * Fetch all notifications for current user
 * @param {Object} params - Query parameters {limit, offset}
 * @returns {Promise<Array>} Array of notification objects
 */
export async function fetchAllNotifications(params = {}) {
  const response = await apiClient.get('notifications/', { params })
  return toNotificationArray(response.data)
}

/**
 * Mark specific notifications as read
 * @param {Array<string>} notificationIds - Array of notification UUIDs
 * @returns {Promise<Object>} Response with unread count
 */
export async function markNotificationsAsRead(notificationIds) {
  const response = await apiClient.post('notifications/mark_as_read/', {
    notification_ids: notificationIds,
  })
  return response.data
}

/**
 * Mark all notifications as read for current user
 * @returns {Promise<Object>} Response confirming action
 */
export async function markAllNotificationsAsRead() {
  const response = await apiClient.post('notifications/mark_all_as_read/')
  return response.data
}

/**
 * Get unread notification count
 * @returns {Promise<number>} Unread count
 */
export async function getUnreadCount() {
  const response = await apiClient.get('notifications/unread_count/')
  return response.data?.count || 0
}
